import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { fmtDateTime } from "../lib";
import { Shell, type ScreenProps } from "./common";

type Item = { productId: string; name: string; article: string | null; issued: number; used: number; left: number };
type ByUser = { user: { id: string; name: string; login: string }; items: Item[] };
type Prod = { id: string; name: string; article: string | null; kind: string };
type Move = { id: string; at: string; qty: number; reason: string; product: string; user: string; note: string | null };

export default function Issue({ updateReady, back, state }: ScreenProps) {
  const [data, setData] = useState<{ byUser: ByUser[]; products: Prod[]; recent: Move[] } | null>(null);
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [userId, setUserId] = useState("");
  const [q, setQ] = useState("");
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(10);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [stock, users] = await Promise.all([
        api<{ byUser: ByUser[]; products: Prod[]; recent: Move[] }>("/api/stock"),
        api<{ users: { id: string; name: string; role: string; active: boolean }[] }>("/api/auth/users"),
      ]);
      setData(stock);
      const reps = users.users.filter((u) => u.role === "rep" && u.active);
      setStaff(reps.map((u) => ({ id: u.id, name: u.name })));
      setUserId((cur) => cur || reps[0]?.id || "");
      setError(null);
    } catch (e: any) {
      setError(e?.status === 0 ? "Нет связи — выдача возможна только онлайн" : e?.message);
      setData({ byUser: [], products: [], recent: [] });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Позиций почти шесть десятков — без поиска в списке не разобраться.
  const found = useMemo(() => {
    const all = data?.products ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all.slice(0, 12);
    return all.filter((p) =>
      (p.name + " " + (p.article ?? "")).toLowerCase().includes(needle)).slice(0, 12);
  }, [data, q]);

  const picked = data?.products.find((p) => p.id === productId) ?? null;

  async function issue() {
    if (!userId) return setError("Некому выдавать — сначала заведи сотрудника");
    if (!productId) return setError("Выбери товар");
    if (!qty) return setError("Укажи количество");
    setBusy(true); setError(null); setDone(null);
    try {
      const res = await api<{ left: number }>("/api/stock/issue", {
        method: "POST",
        body: { userId, productId, qty, note: note.trim() || null },
      });
      const who = staff.find((s) => s.id === userId)?.name ?? "сотруднику";
      setDone(`${qty > 0 ? "Выдано" : "Снято"} ${Math.abs(qty)} — ${picked?.name}. ` +
              `У ${who} теперь на руках: ${res.left}`);
      setNote(""); setQ(""); setProductId("");
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Не удалось записать выдачу");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Выдача раздатки" onBack={back} state={state} updateReady={updateReady}>
      {error && <div className="err">{error}</div>}
      {done && <div className="note" style={{ borderLeftColor: "var(--good)", background: "var(--good-soft)" }}>{done}</div>}

      <div className="block">
        <div className="blockhead"><span className="blocktitle">Выдать сотруднику</span>
          <span className="hint" style={{ marginLeft: "auto" }}>только рекламная продукция</span>
        </div>

        {staff.length === 0 ? (
          <div className="hint">
            Пока нет ни одного медпреда. Заведи сотрудника в разделе «Сотрудники и доступы».
          </div>
        ) : (
          <>
            <div className="field">
              <label className="label" htmlFor="us">Кому <span className="req">*</span></label>
              <select id="us" className="inp" value={userId} onChange={(e) => setUserId(e.target.value)}>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="label" htmlFor="pq">Что <span className="req">*</span></label>
              <input id="pq" className="inp" value={q} placeholder="Начни печатать название или артикул"
                onChange={(e) => { setQ(e.target.value); setProductId(""); }} />
              {!picked && (
                <div className="list" style={{ marginTop: 8, gap: 6 }}>
                  {found.map((p) => (
                    <button className="item" key={p.id} style={{ padding: "10px 12px" }}
                      onClick={() => { setProductId(p.id); setQ(p.name); }}>
                      <span className="main">
                        <span className="name" style={{ fontSize: 14 }}>{p.name}</span>
                        {p.article && <span className="meta">арт. {p.article}</span>}
                      </span>
                    </button>
                  ))}
                  {found.length === 0 && <div className="hint">Ничего не нашлось</div>}
                  {!q && data && data.products.length > 12 &&
                    <div className="hint">Показаны первые 12 позиций — уточни поиском</div>}
                  {data?.products.length === 0 &&
                    <div className="hint">Рекламная продукция ещё не заведена в каталог.</div>}
                </div>
              )}
              {picked && (
                <div className="hint" style={{ color: "var(--good)" }}>
                  Выбрано: {picked.name}{picked.article ? " · арт. " + picked.article : ""}
                </div>
              )}
            </div>

            <div className="row2">
              <div className="field">
                <label className="label" htmlFor="qt">Сколько <span className="req">*</span></label>
                <input id="qt" className="inp" type="number" inputMode="numeric" value={qty}
                  onChange={(e) => setQty(Number(e.target.value) || 0)} />
                <div className="hint">Отрицательное число — забрать обратно или исправить ошибку</div>
              </div>
              <div className="field">
                <label className="label" htmlFor="nt">Комментарий</label>
                <input id="nt" className="inp" value={note} placeholder="Необязательно"
                  onChange={(e) => setNote(e.target.value)} />
              </div>
            </div>

            <button className="btn primary" onClick={issue} disabled={busy || !productId}>
              {busy ? "Записываем…" : qty < 0 ? "Снять с сотрудника" : "Выдать"}
            </button>
          </>
        )}
      </div>

      {data?.byUser.map((u) => (
        <div key={u.user.id}>
          <div className="sub">На руках у {u.user.name}</div>
          {u.items.length === 0 ? (
            <div className="hint">Ничего не числится</div>
          ) : (
            <div className="list">
              {u.items.map((i) => {
                const low = i.issued > 0 && i.left / i.issued < 0.3;
                return (
                  <div className="item" key={i.productId} style={{ cursor: "default" }}>
                    <span className="main">
                      <span className="name">{i.name}</span>
                      <span className="meta">
                        {i.article ? "арт. " + i.article + " · " : ""}
                        выдано {i.issued}, роздано {i.used}
                      </span>
                    </span>
                    <span className={"pill " + (i.left < 0 ? "c" : low ? "w" : "n")}>
                      осталось {i.left}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {data && data.recent.length > 0 && (
        <>
          <div className="sub">Последние движения</div>
          {data.recent.map((m) => (
            <div key={m.id} style={{ display: "flex", gap: 12, padding: "9px 0", borderTop: "1px solid var(--border)" }}>
              <span className="hint" style={{ minWidth: 118, flex: "none" }}>{fmtDateTime(m.at)}</span>
              <span style={{ fontSize: 14, color: "var(--ink-2)" }}>
                <b style={{ color: m.qty > 0 ? "var(--good)" : "var(--ink-2)" }}>
                  {m.qty > 0 ? "+" : ""}{m.qty}
                </b>{" "}
                {m.product} — {m.user}
                {m.reason === "visit" && <span className="hint"> · оставлено в визите</span>}
                {m.note && <span className="hint"> · {m.note}</span>}
              </span>
            </div>
          ))}
        </>
      )}

      <div className="note">
        <b>Здесь только рекламная продукция</b> — пробники и материалы, которые
        сотрудник возит с собой и оставляет в точках. Ассортимент Lacalut сюда
        не попадает: его отмечают на полке в чек-листе выкладки, а не выдают на руки.
      </div>
      <div className="hint">
        Остаток нигде не хранится отдельным числом — он всегда складывается из движений.
        Поэтому любую цифру можно объяснить: видно, что выдано, что роздано по визитам
        и что снято вручную.
      </div>
    </Shell>
  );
}
